import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowUpRight, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { getAdminAccess } from '../components/admin/adminAccess'
import AdminToolNav from '../components/admin/AdminToolNav'
import { ADMIN_TOOLS } from '../components/admin/adminTools'
import { createNadDebug } from '../utils/debugLog'

const adminHubLog = createNadDebug('dw:admin-hub:navigation')

export default function AdminHubPage() {
  const { t } = useTranslation('pages')
  const { user } = useAuth()
  const access = useMemo(() => getAdminAccess(user), [user])
  const visibleTools = ADMIN_TOOLS.filter((tool) => {
    return access[tool.id]
  })

  useEffect(() => {
    const tools: string[] = []
    for (const tool of ADMIN_TOOLS) {
      if (access[tool.id]) tools.push(tool.id)
    }
    adminHubLog({
      event: 'admin-hub:tools-visible',
      feature: 'admin-hub',
      concern: 'navigation',
      tools,
    })
  }, [access])

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6" data-testid="admin-hub-page">
      <AdminToolNav />
      <div className="mb-8 flex flex-col gap-6 border-b border-border-subtle pb-8 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" strokeWidth={2} aria-hidden="true" />
            {t('adminHub.eyebrow')}
          </div>
          <h1 className="text-hero font-semibold tracking-tight text-foreground">
            {t('adminHub.title')}
          </h1>
          <p className="mt-2 text-base leading-relaxed text-muted-foreground">
            {t('adminHub.description')}
          </p>
        </div>
        <div className="w-fit rounded-full border border-border bg-surface-1 px-3 py-1.5 text-xs font-medium text-muted-foreground">
          {t('adminHub.toolCount', { count: visibleTools.length })}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2" data-testid="admin-tool-grid">
        {visibleTools.map((tool, index) => {
          const Icon = tool.icon
          return (
            <motion.div
              key={tool.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.25,
                delay: index * 0.05,
                ease: [0.16, 1, 0.3, 1],
                opacity: { duration: 0.15 },
              }}
            >
              <Link
                to={tool.href}
                onClick={() => {
                  adminHubLog({
                    event: 'admin-hub:tool-opened',
                    feature: 'admin-hub',
                    concern: 'navigation',
                    tool: tool.id,
                  })
                }}
                className="group flex min-h-52 flex-col rounded-xl border border-input-border bg-surface-1 p-5 text-left transition-colors hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface-2 text-muted-foreground transition-colors group-hover:border-primary/50 group-hover:text-primary">
                  <Icon className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                </div>
                <div className="mt-6">
                  <h2 className="text-lg font-medium text-foreground">{t(tool.titleKey)}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {t(tool.descriptionKey)}
                  </p>
                </div>
                <div className="mt-auto flex items-center justify-between border-t border-border-subtle pt-4 text-sm font-medium text-foreground">
                  <span>{t('adminHub.openTool')}</span>
                  <ArrowUpRight
                    className="h-4 w-4 text-text-muted transition-colors group-hover:text-primary"
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                </div>
              </Link>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}
